import Button from 'components/core/button'
import IconButton from 'lib/components/buttons/icon-button'
import Modal, { BaseModalProps } from 'lib/components/modals/modal'
import { X } from 'lucide-react'
import BillingModalPeriod from './billing-modal-period'
import BillingModalPlatform from './billing-modal-platform'
import BillingModalProfessionalServices from './billing-modal-professional-services'
import BillingModalCreativeServices from './billing-modal-creative-services'
import { useEffect, useState } from 'react'
import BillingModalReview from './billing-modal-review'
import { useBillingPlans } from './billing-plans-context'
import { useMutation } from '@tanstack/react-query'
import { request } from 'lib/api/fetch-api'
import { getDowngradeSubscriptionItems, getSubscriptionItems, totalCurrent, totalSelected } from 'lib/util/billing'
import { PickleSubscription } from 'interfaces/pickle-subscription'
import { toast } from 'lib/components/toast/toast'

export default function BillingModal({
  open,
  setOpen,
}: {
  open: BaseModalProps['open']
  setOpen: BaseModalProps['setOpen']
}) {
  const { brightback, currentSubscriptions, selectedPlans, updateCurrentSubscriptions } = useBillingPlans()
  const [review, setReview] = useState(false)
  const [checked, setChecked] = useState(false)
  const [cancel, setCancel] = useState(false)
  const disabled =
    currentSubscriptions?.creativeSubscription?.picklePlan?.id === selectedPlans?.creativePlan?.id &&
    currentSubscriptions?.platformSubscription?.picklePlan?.id === selectedPlans?.platformPlan?.id &&
    currentSubscriptions?.professionalSubscription?.picklePlan?.id === selectedPlans?.professionalPlan?.id
  const isUpgrade = totalSelected(selectedPlans) >= totalCurrent(currentSubscriptions)
  const items = isUpgrade
    ? getSubscriptionItems(currentSubscriptions, selectedPlans)
    : getDowngradeSubscriptionItems(selectedPlans)
  const hasScheduledUpdate = Object.values(currentSubscriptions).some((subscription) => subscription?.updateScheduled)

  const createSubscriptionMutation = useMutation<PickleSubscription[]>({
    mutationFn: () => {
      return request({
        endpoint: isUpgrade ? 'createUpgrade' : 'createDowngrade',
        body: {
          stripe_subscription_id: currentSubscriptions?.platformSubscription?.stripeSubscriptionId || '',
          subscription_items: items,
        },
      })
    },
    onSuccess: (data) => {
      handleClose()
      updateCurrentSubscriptions(data)
      toast.success('Subscription updated successfully')
    },
    onError: (error) => {
      toast.error('Error updating subscription')
      console.error('Error creating subscription', error)
    },
  })

  useEffect(() => {
    if (open) setReview(false)
  }, [open])

  const handleClose = () => {
    setOpen(false)
    setReview(false)
    setChecked(false)
    setCancel(false)
  }

  const handleCancel = () => {
    if (!window.Brightback) {
      console.warn('Brightback not loaded')
      return
    }

    const promise = window.Brightback.handleDataPromise(brightback)

    promise.then((result) => {
      if (result.valid && result.url) {
        window.location.href = result.url
      } else {
        toast.error('There was an issue with the cancellation process. Please try again later.')
      }
    })
  }

  return (
    <Modal open={open} setOpen={handleClose} size="lg">
      <Modal.Header as="div">
        <div className="tw-flex tw-items-center tw-justify-between">
          <h4>Manage Subscription</h4>
          <IconButton color="secondary" onClick={handleClose} data-testid="close-modal-button">
            <X />
          </IconButton>
        </div>
      </Modal.Header>

      <Modal.Body>
        {hasScheduledUpdate ? (
          <ScheduledUpdateText />
        ) : cancel ? (
          <CancellationText />
        ) : review ? (
          <BillingModalReview checked={checked} setChecked={setChecked} />
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <BillingModalPeriod />
            <BillingModalPlatform />
            <div className="tw-font-semibold">Services</div>
            <BillingModalCreativeServices />
            <BillingModalProfessionalServices />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="tw-mt-8 tw-flex tw-w-full tw-justify-between">
          {!review && !hasScheduledUpdate && !cancel ? (
            <Button color="neutral" onClick={() => setCancel(true)}>
              Cancel Subscription
            </Button>
          ) : (
            <div />
          )}
          {review ? (
            <div className="tw-flex tw-justify-end">
              <Button
                color="neutral"
                onClick={() => setReview(false)}
                className="tw-mr-2"
                disabled={createSubscriptionMutation.isPending}
              >
                Go Back
              </Button>
              <Button
                color="purple"
                onClick={() => createSubscriptionMutation.mutate()}
                disabled={createSubscriptionMutation.isPending || disabled || !checked}
              >
                {createSubscriptionMutation.isPending ? 'Updating...' : 'Confirm changes'}
              </Button>
            </div>
          ) : cancel ? (
            <div className="tw-flex tw-justify-end">
              <Button color="neutral" onClick={() => setCancel(false)} className="tw-mr-2">
                Go Back
              </Button>
              <Button color="transparent" onClick={handleCancel}>
                Continue to cancel
              </Button>
              <a href="#" id="bb-cancel" className="tw-hidden" />
            </div>
          ) : (
            <div className="tw-flex tw-justify-end">
              <Button
                color="neutral"
                onClick={() => {
                  setOpen(false)
                }}
                className="tw-mr-2"
                disabled={createSubscriptionMutation.isPending}
              >
                Cancel
              </Button>
              <Button color="purple" onClick={() => setReview(true)} disabled={disabled}>
                Review changes
              </Button>
            </div>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

function ScheduledUpdateText() {
  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-text-center tw-text-peppercorn-900">
      <div className="tw-text-lg">Oh Pickles!</div>
      <div className="tw-w-96">
        Because this subscription has a scheduled update, please contact our wonderful Customer Support team at{' '}
        <a href="mailto:help@designpickle.com" className="tw-text-cornflower-500">
          help@designpickle.com
        </a>{' '}
        for help in managing this subscription.
      </div>
    </div>
  )
}

function CancellationText() {
  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-text-center tw-text-peppercorn-900">
      <div className="tw-text-lg">Are you sure you want to cancel?</div>
      <div className="tw-w-96">
        By cancelling your account you will lose access to the Design Pickle platform and any services you are
        subscribed to. You will no longer be able to access your design requests or files.
      </div>
    </div>
  )
}
